/**
 * B94 — the platform's own §11 corner: operations that belong to the
 * operator console rather than to any tenant (RULING 2026-09-21, Pathik).
 *
 * The one law this file lives under: a platform operation is reachable ONLY
 * by an OPERATOR principal, and only through the `platform` marking the
 * authorizer and the kernel both read. Nothing here widens a tenant door.
 */
import { z } from "zod";
import { fleetReadOp, fleetWriteOp, tenantCrossingWriteOp, zEpochMs, zId, zMoney } from "./common.js";

const zTenantRow = z
  .object({
    tenantId: zId,
    name: z.string().min(1),
    /** The business's own site, shown under its name on the frame. */
    site: z.string().min(1).optional(),
    type: z.enum(["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"]),
    lifecycle: z.enum(["LIVE", "DEMO"]),
    runState: z.enum(["ON", "PAUSED", "PROVISIONING"]),
    pausedReason: z.string().min(1).optional(),
    channelCount: z.number().int().nonnegative(),
    createdAt: zEpochMs,
    demoExpiresAt: zEpochMs.optional(),
    /** Whole days until the sweep takes it; DEMO rows only. */
    demoDaysLeft: z.number().int().nonnegative().optional(),
    /**
     * B94 — SALES · MTD: net sales since the first of the UTC month, one
     * entry per currency sold in. An EMPTY list is "nothing sold"; an ABSENT
     * field is "this stage reads no sales facts" — the two faces differ.
     */
    salesToDate: z.array(zMoney).optional(),
    /** B94 — the ALERTS column: counted from platform.alerts' own alerts. Absent = not wired. */
    alerts: z
      .object({ open: z.number().int().nonnegative(), critical: z.number().int().nonnegative() })
      .strict()
      .optional(),
  })
  .strict();

/**
 * tenant.list — every business this operator may see, and the four numbers
 * the frame's KPI strip shows. The scope filter is the handler's job and is
 * NOT expressible here: a response schema cannot know who asked.
 *
 * The money and alert columns of frame 322:2 (B94, 2026-09-23) ride each
 * row as OPTIONAL fields: present only where the stage wires their source
 * (the sales facts; the same alert rollup platform.alerts answers). A zero
 * we invented would read as a fact to the person deciding whether a tenant
 * is healthy, so an unwired source is an absent field, never a 0.
 */
export const tenantList = fleetReadOp({
  name: "tenant.list",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: {
    /** The frame's ALL / LIVE / DEMO pills. Absent = ALL. */
    lifecycle: z.enum(["LIVE", "DEMO"]).optional(),
  },
  response: z
    .object({
      tenants: z.array(zTenantRow),
      summary: z
        .object({
          total: z.number().int().nonnegative(),
          live: z.number().int().nonnegative(),
          demo: z.number().int().nonnegative(),
          expiringSoon: z.number().int().nonnegative(),
          paused: z.number().int().nonnegative(),
        })
        .strict(),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    tenants: [
      {
        tenantId: "blue-harbor",
        name: "Blue Harbor Ferries",
        site: "blueharborferries.com",
        type: "FERRY",
        lifecycle: "LIVE",
        runState: "ON",
        channelCount: 3,
        createdAt: 1_780_000_000_000,
      },
    ],
    summary: { total: 1, live: 1, demo: 0, expiringSoon: 0, paused: 0 },
  },
});

/* ------------------------------------------------------------------------- */
/* B94 leg (c) — Users & access (frame 337-2): the platform's OWN seats.     */
/* ------------------------------------------------------------------------- */

export const zOperatorRole = z.enum(["ROOT", "OPERATOR", "SALES", "SUPPORT"]);

const zOperatorUser = z
  .object({
    /** The pool's stable id (sub) — the self row is found by it. */
    operatorId: zId,
    email: z.string().min(3),
    name: z.string().min(1).optional(),
    role: zOperatorRole,
    /** The RAW custom:tenants claim: `*` | `demos` | `<id>,<id>`. */
    tenants: z.string().min(1),
    enabled: z.boolean(),
    confirmed: z.boolean(),
    /** Absent = the provider could not say (never invented). */
    mfaOn: z.boolean().optional(),
    createdAt: zEpochMs.optional(),
    /** FORCE_CHANGE_PASSWORD only: when the unaccepted invite dies. */
    invitedExpiresAt: zEpochMs.optional(),
  })
  .strict();

const EXAMPLE_OPERATOR = {
  operatorId: "a1b2c3d4-op",
  email: "maya@transact.example",
  name: "Maya Chen",
  role: "SALES",
  tenants: "demos",
  enabled: true,
  confirmed: true,
  mfaOn: true,
};

/** Every seat — any operator may look; only ROOT may change (the doors below). */
export const operatorUserList = fleetReadOp({
  name: "operator.users.list",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: {},
  response: z.object({ users: z.array(zOperatorUser) }).strict(),
  exampleRequest: {},
  exampleResponse: { users: [EXAMPLE_OPERATOR] },
});

/** Frame matrix, last row: "Invite, disable and change these users" — ROOT only. */
export const operatorUserInvite = fleetWriteOp({
  name: "operator.users.invite",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT"],
  fields: {
    email: z.string().min(3).max(320),
    role: zOperatorRole,
    tenants: z.string().min(1).max(2000),
    name: z.string().min(1).max(120).optional(),
  },
  response: z
    .object({
      user: zOperatorUser,
      /** Shown ONCE (no SES yet) — never stored, never re-readable. */
      temporaryPassword: z.string().min(1),
    })
    .strict(),
  exampleRequest: { email: "maya@transact.example", role: "SALES", tenants: "demos" },
  exampleResponse: { user: { ...EXAMPLE_OPERATOR, confirmed: false, mfaOn: false }, temporaryPassword: "shown-once" },
});

export const operatorUserUpdate = fleetWriteOp({
  name: "operator.users.update",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT"],
  fields: {
    operatorId: zId,
    role: zOperatorRole.optional(),
    tenants: z.string().min(1).max(2000).optional(),
  },
  response: z.object({ user: zOperatorUser }).strict(),
  exampleRequest: { operatorId: "a1b2c3d4-op", role: "SUPPORT" },
  exampleResponse: { user: { ...EXAMPLE_OPERATOR, role: "SUPPORT" } },
});

export const operatorUserDisable = fleetWriteOp({
  name: "operator.users.disable",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT"],
  fields: { operatorId: zId },
  response: z.object({ user: zOperatorUser }).strict(),
  exampleRequest: { operatorId: "a1b2c3d4-op" },
  exampleResponse: { user: { ...EXAMPLE_OPERATOR, enabled: false } },
});

export const operatorUserEnable = fleetWriteOp({
  name: "operator.users.enable",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT"],
  fields: { operatorId: zId },
  response: z.object({ user: zOperatorUser }).strict(),
  exampleRequest: { operatorId: "a1b2c3d4-op" },
  exampleResponse: { user: EXAMPLE_OPERATOR },
});

export const operatorUserRemove = fleetWriteOp({
  name: "operator.users.remove",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT"],
  fields: { operatorId: zId },
  response: z.object({ removed: z.literal(true) }).strict(),
  exampleRequest: { operatorId: "a1b2c3d4-op" },
  exampleResponse: { removed: true },
});

/* ------------------------------------------------------------------------- */
/* B501 — the demo lifecycle (frames 324-2 builder, 328-2 detail).           */
/*                                                                           */
/* A demo tenant is a real tenant on the stage, born so a sales rep can show  */
/* a prospect their OWN catalogue running on Transact, and wiped when the     */
/* conversation ends. Three facts shape every contract below:                */
/*                                                                           */
/*  - the DELETE FENCE is not expressible in a schema. "Is this a demo, and   */
/*    does it carry a record?" is a question about stored state, so the       */
/*    contract carries only the confirmation the HUMAN must repeat, and the   */
/*    handler holds the rest (`demoDeleteRefusal`, one definition, in         */
/*    @transact/domain).                                                     */
/*  - a wipe is IRREVERSIBLE, so `demo.delete` reports what it removed        */
/*    rather than a bare `ok`. The operator who typed the name deserves to    */
/*    read what their sentence cost.                                          */
/*  - the BUILDER's two doors (`demo.propose`, `demo.provision`) carry the    */
/*    PROPOSAL across the wire rather than a server-held draft, because       */
/*    `demo.propose` stores nothing — the frame's own banner says so          */
/*    ("Nothing has been created yet"). The rep may edit what it proposed,    */
/*    and what they approve is what travels back; the ids in the plan it      */
/*    becomes are derived at the door, never carried in the request.          */
/* ------------------------------------------------------------------------- */

const zDemoRow = z
  .object({
    tenantId: zId,
    slug: z.string().min(1),
    /** The prospect, as the rep typed it. */
    clientName: z.string().min(1),
    /** The prospect's own website — what the builder browsed. */
    websiteUrl: z.string().min(1),
    siteKey: z.string().min(1),
    adminEmail: z.string().min(3),
    createdAt: zEpochMs,
    /** The operator who provisioned it — every demo has a name against it. */
    createdBy: z.string().min(1),
    /** Absent = it stands until someone says otherwise, never "expired now". */
    expiresAt: zEpochMs.optional(),
    /** Whole days until the sweep takes it; absent when `expiresAt` is. */
    daysLeft: z.number().int().nonnegative().optional(),
    handedOverAt: zEpochMs.optional(),
    handedOverTo: z.string().min(1).optional(),
  })
  .strict();

const EXAMPLE_DEMO = {
  tenantId: "demo-blue-harbor",
  slug: "blue-harbor",
  clientName: "Blue Harbor Ferries",
  websiteUrl: "https://blueharborferries.com",
  siteKey: "sk-blueharbordemo2026",
  adminEmail: "rep@transact.example",
  createdAt: 1_780_000_000_000,
  createdBy: "maya@transact.example",
};

/**
 * demo.list — the demos this operator may see. Scoped in the handler by
 * `custom:tenants` exactly as `tenant.list` is: a SALES seat scoped to
 * `demos` sees every demo, a seat scoped to a list sees only its own.
 */
export const demoList = fleetReadOp({
  name: "demo.list",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: {},
  response: z.object({ demos: z.array(zDemoRow) }).strict(),
  exampleRequest: {},
  exampleResponse: { demos: [EXAMPLE_DEMO] },
});

/**
 * demo.get — frame 328-2's detail.
 *
 * THE SUBJECT IS `demoId`, NOT `tenantId`, AND THAT IS DELIBERATE. A demo's
 * identifier is a tenant id, so `tenantCrossingReadOp` looks like the right
 * shape and is not: it hands the scope wall to the KERNEL, which answers an
 * out-of-scope id with 403 TENANT_MISMATCH — telling a SALES seat scoped to
 * three demos that a fourth id it guessed is real. These doors answer the
 * UNIFORM MISS instead: out-of-scope and non-existent are the same 404 with
 * the same sentence, enforced in the handler. Naming the field `demoId`
 * keeps that honest rather than declaring a tenant scoping these doors do
 * not use.
 */
export const demoGet = fleetReadOp({
  name: "demo.get",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: { demoId: zId },
  response: z.object({ demo: zDemoRow }).strict(),
  exampleRequest: { demoId: "demo-blue-harbor" },
  exampleResponse: { demo: EXAMPLE_DEMO },
});

/**
 * demo.handover — the rep gives the demo to the prospect. Recorded rather
 * than performed: there is no mail channel yet (B63), so this stamps WHO it
 * went to and WHEN, and the console shows the links to copy. A second
 * hand-over is refused rather than silently re-sent.
 *
 * SALES may hand over — it is the job. SUPPORT may not: a support seat
 * reads, it does not give a tenant away.
 */
export const demoHandover = fleetWriteOp({
  name: "demo.handover",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT", "OPERATOR:OPERATOR", "OPERATOR:SALES"],
  fields: {
    demoId: zId,
    /** Who it was given to, for the record. */
    handedOverTo: z.string().min(3).max(320),
  },
  response: z.object({ demo: zDemoRow }).strict(),
  exampleRequest: { clientKey: "ck-handover-1", demoId: "demo-blue-harbor", handedOverTo: "ops@blueharborferries.com" },
  exampleResponse: { demo: { ...EXAMPLE_DEMO, handedOverAt: 1_780_000_100_000, handedOverTo: "ops@blueharborferries.com" } },
});

/**
 * demo.delete — the wipe, behind the fence. `confirm` must repeat the exact
 * tenant id: the door asks the operator to type the name twice for the same
 * reason the script does, and the comparison is EXACT, because a pasted id
 * with a stray space is a paste that went wrong.
 *
 * NOT admitted: SALES and SUPPORT. A demo is real data — orders,
 * entitlements, an audit trail — and the seat that creates one is not
 * automatically the seat that may destroy it. (Judgment recorded for
 * Pathik to overrule; reversal is two strings in this list.)
 */
export const demoDelete = fleetWriteOp({
  name: "demo.delete",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT", "OPERATOR:OPERATOR"],
  fields: {
    demoId: zId,
    /** Must equal `demoId`, character for character. */
    confirm: z.string().min(1),
    /** Report what WOULD go and touch nothing — the script's `--dry-run`. */
    dryRun: z.boolean().optional(),
  },
  /**
   * RULED 2026-09-23 (worker Lambda): the door checks the fence and the
   * typed confirmation, then hands the wipe — dry run included, because
   * the inventory is the same table scan — to the worker as a job. What
   * was (or would be) removed lands on the job row; the console polls.
   */
  response: z
    .object({
      tenantId: zId,
      dryRun: z.boolean(),
      jobId: zId,
    })
    .strict(),
  exampleRequest: { clientKey: "ck-wipe-1", demoId: "demo-blue-harbor", confirm: "demo-blue-harbor" },
  exampleResponse: {
    tenantId: "demo-blue-harbor",
    dryRun: false,
    jobId: "dj_000001780000000100_w9pz2",
  },
});

/* ------------------------------------------ B501 worker leg: the job poll */

/* The PROPOSAL schema lives ABOVE the job schema deliberately (B544): a
   DONE READ job carries the proposal, and the MCP membrane cannot
   translate a z.lazy - so the reference is direct, which needs the order. */
const zConfidence = z.enum(["HIGH", "CHECK"]);
/** A site-relative path the browse actually read — the citation under a card. */
const zSourcePath = z.array(z.string().min(1));

const zProposalProduct = z
  .object({
    name: z.string().min(1),
    timed: z.boolean(),
    capacityNote: z.string().min(1).optional(),
    ticketTypes: z
      .array(
        z
          .object({
            label: z.string().min(1),
            /** Integer minor units, as everywhere: money is never a float. */
            priceMinor: z.number().int().positive(),
            note: z.string().min(1).optional(),
          })
          .strict(),
      )
      .min(1),
    whenHeadline: z.string().min(1),
    whenDetail: z.string().min(1).optional(),
    confidence: zConfidence,
    sources: zSourcePath,
    /** B521: the pictures and words lifted from the product's own page — always CHECK. */
    media: z
      .object({
        photos: z
          .array(
            z
              .object({ url: z.string().url().regex(/^https:\/\//i, "a photo must be an https URL"), alt: z.string().min(1).optional() })
              .strict(),
          )
          .max(6),
        description: z.string().min(1).max(600).optional(),
        descriptionSource: z.string().min(1).optional(),
        confidence: z.literal("CHECK"),
      })
      .strict()
      .optional(),
  })
  .strict();

const zProposal = z
  .object({
    websiteUrl: z.string().min(1),
    host: z.string().min(1),
    slug: z.string().min(1),
    business: z
      .object({
        name: z.string().min(1),
        type: z.enum(["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"]).optional(),
        timeZone: z.string().min(1).optional(),
        brandColors: z.array(z.string().min(1)),
        initials: z.string().min(1),
        confidence: zConfidence,
        sources: zSourcePath,
        /** B544: the booking platform their markup names ("They sell through Ventrata today"). */
        sellsThrough: z.string().min(1).optional(),
      })
      .strict(),
    products: z.array(zProposalProduct),
    promotion: z
      .object({
        code: z.string().min(1),
        percentOff: z.number().int().positive(),
        headline: z.string().min(1),
        confidence: zConfidence,
        sources: zSourcePath,
      })
      .strict()
      .optional(),
    scheduleDays: z.number().int().positive(),
    /**
     * What the prices are in. The reader says it; the screen never guesses.
     * `min(3)` rather than `length(3)`: the MCP membrane translates min and
     * not length, and a contract the membrane cannot translate is an
     * operation the planner cannot see (packages/mcp-server/src/schema.ts).
     */
    currency: z.string().min(3),
    browsed: z
      .object({
        pages: z.number().int().nonnegative(),
        elapsedMs: z.number().int().nonnegative(),
        products: z.number().int().nonnegative(),
        promotions: z.number().int().nonnegative(),
        /** B558 (5): the shape read — distinct buy cards and price rows. Absent on older receipts. */
        buyButtons: z.number().int().nonnegative().optional(),
        priceRows: z.number().int().nonnegative().optional(),
      })
      .strict(),
    /** B544: how the sources were read. Absent = raw. */
    readMode: z.enum(["raw", "rendered"]).optional(),
    /** B586: who drew the draft — the assistant (the model) or the plain reader. Absent = not recorded. */
    drafter: z.enum(["assistant", "reader"]).optional(),
    /** What the reader refused to carry, in words the rep can act on. */
    warnings: z.array(z.string().min(1)),
  })
  .strict();

const zDemoJobOutcomeWarnings = z.array(z.string().min(1));

const zDemoJob = z
  .object({
    jobId: zId,
    kind: z.enum(["PROVISION", "WIPE", "READ"]),
    tenantId: zId,
    state: z.enum(["QUEUED", "RUNNING", "DONE", "FAILED"]),
    requestedAt: z.number().int().nonnegative(),
    startedAt: z.number().int().nonnegative().optional(),
    finishedAt: z.number().int().nonnegative().optional(),
    dryRun: z.boolean().optional(),
    /** DONE provision: the receipt, credential-free by construction. */
    provisioned: z
      .object({
        links: z
          .object({
            website: z.string().min(1).optional(),
            storefront: z.string().min(1).optional(),
            backOffice: z.string().min(1).optional(),
            counter: z.string().min(1).optional(),
          })
          .strict(),
        adminEmail: z.string().min(3),
        steps: z.number().int().nonnegative(),
        channels: z.number().int().nonnegative(),
        warnings: zDemoJobOutcomeWarnings,
      })
      .strict()
      .optional(),
    /** DONE wipe (or dry run): what went / would go. */
    wiped: z
      .object({
        rows: z.number().int().nonnegative(),
        users: z.number().int().nonnegative(),
        siteObjects: z.number().int().nonnegative(),
        warnings: zDemoJobOutcomeWarnings,
      })
      .strict()
      .optional(),
    /** B544, READ only: the address the worker renders. */
    websiteUrl: z.string().min(1).optional(),
    /** B544, DONE read: the proposal the rendered browse produced. */
    read: z.object({ proposal: zProposal }).strict().optional(),
    /** B544: the worker's one-line progress ("Reading their site - 3 of 8 pages"). */
    note: z.string().min(1).optional(),
    /** FAILED: one sentence a rep can read. */
    why: z.string().min(1).optional(),
  })
  .strict();

/**
 * demo.job.get — the poll behind the builder's DONE face and the detail's
 * wipe. Reads only; the uniform miss covers jobs outside the caller's
 * scope exactly as `demo.get` covers demos.
 */
export const demoJobGet = fleetReadOp({
  name: "demo.job.get",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: { jobId: zId },
  response: z.object({ job: zDemoJob }).strict(),
  exampleRequest: { jobId: "dj_000001780000000000_k3xq1" },
  exampleResponse: {
    job: {
      jobId: "dj_000001780000000000_k3xq1",
      kind: "PROVISION" as const,
      tenantId: "demo-blue-harbor",
      state: "DONE" as const,
      requestedAt: 1_780_000_000_000,
      startedAt: 1_780_000_001_000,
      finishedAt: 1_780_000_090_000,
      provisioned: {
        links: { website: "https://harness.example/demo/blue-harbor" },
        adminEmail: "demo-admin+blue-harbor@transact.example",
        steps: 21,
        channels: 2,
        warnings: [],
      },
    },
  },
});

/* ------------------------------------------------------------------------- */
/* B501 — the AI demo builder (frame 324-2).                                 */
/*                                                                           */
/* THE SHAPE, AND WHY IT IS THIS ONE. `demo.propose` browses a stranger's    */
/* website and returns a whole tenant as a PROPOSAL; it writes nothing, so   */
/* it is a read and carries no clientKey. `demo.provision` takes that        */
/* proposal back — edited by the rep or not — plus the handful of answers    */
/* the website could not give, and builds the plan SERVER-SIDE.              */
/*                                                                           */
/* What the request deliberately CANNOT carry: any id. Not the tenant id,    */
/* not the site key, not a product id. They are derived from the slug and    */
/* the ordinal in @transact/domain and re-derived at the door, because a     */
/* request that could name its own tenant would make "browse this URL" a     */
/* way into `dev-ferry`'s partition. `createdBy` is likewise the PRINCIPAL's */
/* and is not a field: a demo carries the name of the seat that made it.     */
/* ------------------------------------------------------------------------- */


const EXAMPLE_PROPOSAL = {
  websiteUrl: "https://blueharborferries.com",
  host: "blueharborferries.com",
  slug: "blue-harbor",
  business: {
    name: "Blue Harbor Ferries",
    type: "FERRY" as const,
    timeZone: "America/Los_Angeles",
    brandColors: ["#0e7490"],
    initials: "BH",
    confidence: "HIGH" as const,
    sources: ["/about"],
  },
  products: [
    {
      name: "Sunset Cruise",
      timed: true,
      ticketTypes: [{ label: "Adult", priceMinor: 4500 }],
      whenHeadline: "Daily 17:30 and 19:00",
      confidence: "HIGH" as const,
      sources: ["/tickets"],
    },
  ],
  scheduleDays: 90,
  currency: "USD",
  browsed: { pages: 14, elapsedMs: 48_000, products: 1, promotions: 0 },
  warnings: [],
};

/**
 * demo.propose — browse the prospect's site and draft a tenant from it.
 *
 * A READ, and the frame says why in one line above the fold: nothing has
 * been created yet. Admitted to SALES as well as ROOT and OPERATOR —
 * building a prospect's demo is the sales seat's whole job, and a door
 * that creates nothing is the one to be generous with.
 */
export const demoPropose = fleetReadOp({
  name: "demo.propose",
  permission: "platform:read",
  principals: ["OPERATOR:ROOT", "OPERATOR:OPERATOR", "OPERATOR:SALES"],
  fields: {
    websiteUrl: z.string().min(1),
    /** B544: ask for the rendered browse outright (a shell root asks for it by itself). */
    render: z.boolean().optional(),
  },
  /**
   * B544 - ONE of the two: a raw browse answers the proposal in-line; a
   * site that draws its pages in the browser (or render: true) answers a
   * READ job instead, and the console polls `demo.job.get` for the
   * proposal the worker's rendered browse produces.
   */
  response: z
    .object({
      proposal: zProposal.optional(),
      readJob: z.object({ jobId: zId }).strict().optional(),
    })
    .strict(),
  exampleRequest: { websiteUrl: "https://blueharborferries.com" },
  exampleResponse: { proposal: EXAMPLE_PROPOSAL },
});

/**
 * demo.provision — approve a proposal, and a tenant is born.
 *
 * `choices` are the answers a website does not give: what kind of business
 * this is, which clock it runs on, and when the sweep should take the demo
 * back. They OVERRIDE the proposal rather than merging with it, because
 * they are what the rep looked at the screen and decided.
 *
 * The response carries the admin password ONCE and nothing stores it —
 * the same custody the provisioning script has always had, moved to the
 * seat that approved the plan. A port that cannot mint one leaves the
 * field out rather than sending an empty string.
 */
export const demoProvision = fleetWriteOp({
  name: "demo.provision",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT", "OPERATOR:OPERATOR", "OPERATOR:SALES"],
  fields: {
    proposal: zProposal,
    choices: z
      .object({
        clientName: z.string().min(1).optional(),
        slug: z.string().min(1).optional(),
        adminEmail: z.string().min(3).optional(),
        type: z.enum(["FERRY", "SHOW", "PARK", "ATTRACTION", "RETAIL"]),
        timeZone: z.string().min(1),
        /** Whole days, or NEVER — the frame's 7D · 14D · 30D · NEVER row. */
        expiryDays: z.union([z.number().int().positive(), z.literal("NEVER")]).optional(),
      })
      .strict(),
  },
  /**
   * RULED 2026-09-23 (worker Lambda): the approve ANSWERS, it does not
   * perform. The demo comes back PROVISIONING with the job the worker will
   * write its outcome onto; the console polls `demo.job.get`. No password
   * rides any response — the admin's credential is a force-change
   * temporary secret delivered by Cognito's own invite.
   */
  response: z
    .object({
      demo: zDemoRow,
      jobId: zId,
    })
    .strict(),
  exampleRequest: {
    clientKey: "ck-provision-1",
    proposal: EXAMPLE_PROPOSAL,
    choices: { type: "FERRY" as const, timeZone: "America/Los_Angeles", expiryDays: 14 },
  },
  exampleResponse: {
    demo: EXAMPLE_DEMO,
    jobId: "dj_000001780000000000_k3xq1",
  },
});

/* --------------------------------------------- B94 leg (c2): frame 331-2 */

const zInvoiceState = z.enum(["NOT_SENT", "DUE", "OVERDUE", "PAID", "UNCOLLECTIBLE", "VOIDED"]);

/**
 * THERE IS NO HUMAN INVOICE NUMBER HERE, AND THAT IS DELIBERATE.
 *
 * The frame's rail draws `INV-0431 · Sep` beneath each business. Our
 * invoice ids are DERIVED from the period (`invoiceIdFor`), so there is no
 * `INV-0431` anywhere in this platform to look up — minting one for the
 * screen would put a reference on an operator's desk that no query, no
 * support call and no tenant's own copy could ever resolve. The period is
 * what identifies the document, so the period is what the row carries, and
 * the console spells it in words.
 */
const zInvoiceRow = z
  .object({
    tenantId: zId,
    /** How an operator says the business. The id is the systemId, never the face. */
    name: z.string().min(1),
    invoiceId: zId,
    period: z.object({ startMs: zEpochMs, endMs: zEpochMs }).strict(),
    total: zMoney,
    state: zInvoiceState,
    nothingDue: z.boolean(),
    dueAtMs: zEpochMs.optional(),
    paidAtMs: zEpochMs.optional(),
    daysLate: z.number().int().positive().optional(),
    remindersSent: z.number().int().nonnegative().optional(),
  })
  .strict();

const EXAMPLE_INVOICE = {
  tenantId: "blue-harbor",
  name: "Blue Harbor Ferries",
  invoiceId: "invoice 1788220800000 1790899200000",
  period: { startMs: 1_788_220_800_000, endMs: 1_790_899_200_000 },
  total: { amount: 21_480_00, currency: "USD" },
  state: "PAID" as const,
  nothingDue: false,
  dueAtMs: 1_791_072_000_000,
  paidAtMs: 1_791_000_000_000,
  remindersSent: 0,
};

/**
 * platform.invoices — every invoice this operator may see, across
 * businesses, with the rail's own figures.
 *
 * The summary totals are ARRAYS of money, one entry per currency, because
 * two businesses can bill in different ones and a single added figure would
 * be a number with no meaning. A console that showed one anyway would be
 * inventing an exchange rate.
 */
export const platformInvoices = fleetReadOp({
  name: "platform.invoices",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: {},
  response: z
    .object({
      invoices: z.array(zInvoiceRow),
      summary: z
        .object({
          billedThisMonth: z.array(zMoney),
          outstanding: z.array(zMoney),
          overdueCount: z.number().int().nonnegative(),
          notSentCount: z.number().int().nonnegative(),
          tenantsBilled: z.number().int().nonnegative(),
          tenantsNeverBilled: z.number().int().nonnegative(),
          monthStartMs: zEpochMs,
        })
        .strict(),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    invoices: [EXAMPLE_INVOICE],
    summary: {
      billedThisMonth: [{ amount: 48_200_00, currency: "USD" }],
      outstanding: [{ amount: 6_100_00, currency: "USD" }],
      overdueCount: 3,
      notSentCount: 0,
      tenantsBilled: 5,
      tenantsNeverBilled: 1,
      monthStartMs: 1_790_899_200_000,
    },
  },
});

const zAlertClass = z.enum(["INVOICE_OVERDUE", "DEMO_EXPIRING", "FRAUD_PATTERN", "CONNECTOR_DOWN"]);
const zVelocityDimension = z.enum(["source", "buyer"]);

const zAlert = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal("INVOICE_OVERDUE"),
      id: zId,
      severity: z.enum(["CRITICAL", "ATTENTION"]),
      tenantId: zId,
      name: z.string().min(1),
      invoiceId: zId,
      amount: zMoney,
      daysLate: z.number().int().nonnegative(),
      dueAtMs: zEpochMs,
      remindersSent: z.number().int().nonnegative(),
    })
    .strict(),
  z
    .object({
      kind: z.literal("DEMO_EXPIRING"),
      id: zId,
      severity: z.enum(["CRITICAL", "ATTENTION"]),
      tenantId: zId,
      name: z.string().min(1),
      daysLeft: z.number().int().nonnegative(),
      expiresAt: zEpochMs,
    })
    .strict(),
  /* B94 — B500's observational velocity flags (ruled: a flag, never a
     block). Severity is ATTENTION by construction; the order ids name
     what to open, never who bought. */
  z
    .object({
      kind: z.literal("FRAUD_PATTERN"),
      id: zId,
      severity: z.literal("ATTENTION"),
      tenantId: zId,
      name: z.string().min(1),
      flaggedOrders: z.number().int().positive(),
      peakCount: z.number().int().positive(),
      dimensions: z.array(zVelocityDimension).min(1),
      latestAt: zEpochMs,
      orderIds: z.array(zId).min(1),
      lookbackMs: z.number().int().positive(),
    })
    .strict(),
]);

/**
 * platform.alerts — frame 331-2's main column.
 *
 * `unwatched` is part of the CONTRACT, not a note in the handler. The frame
 * draws four classes and this platform holds a source for two; a response
 * that returned only `alerts` would let an empty column read as an
 * all-clear over classes nobody consulted. `ops.alerts` already answers
 * this way (its `skipped`), and for the same reason.
 */
export const platformAlertsOp = fleetReadOp({
  name: "platform.alerts",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: {},
  response: z
    .object({
      alerts: z.array(zAlert),
      summary: z
        .object({
          open: z.number().int().nonnegative(),
          critical: z.number().int().nonnegative(),
        })
        .strict(),
      watched: z.array(zAlertClass),
      unwatched: z.array(zAlertClass),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    alerts: [
      {
        kind: "DEMO_EXPIRING" as const,
        id: "DEMO_EXPIRING demo-harbor-lights",
        severity: "ATTENTION" as const,
        tenantId: "demo-harbor-lights",
        name: "Harbor Lights Cruises",
        daysLeft: 3,
        expiresAt: 1_791_244_800_000,
      },
    ],
    summary: { open: 1, critical: 0 },
    watched: ["INVOICE_OVERDUE" as const, "DEMO_EXPIRING" as const],
    unwatched: ["FRAUD_PATTERN" as const, "CONNECTOR_DOWN" as const],
  },
});

/**
 * platform.activity — frame 331-2's AGENTS rail: the platform activity
 * feed B527 built (ALARM episodes the intake opens and OK closes; AGENT
 * rows for what the triage agent — and later a session — did). Bounded and
 * newest-first by the feed's own id order; `resolvedToday` is counted over
 * exactly the rows returned, and the response says so (`over`), because a
 * count over rows nobody fetched would be an all-clear nobody measured.
 */
const zActivityEpisode = z
  .object({
    activityId: zId,
    kind: z.enum(["ALARM", "AGENT", "DEMO", "OPERATOR", "CLIENT_ERROR"]),
    state: z.enum(["OPEN", "RESOLVED"]),
    title: z.string().min(1),
    /** The alarm's readable stem, when the episode is an alarm's. */
    alarm: z.string().min(1).optional(),
    openedAt: z.number().int().nonnegative(),
    resolvedAt: z.number().int().nonnegative().optional(),
    /** What triage filed, when it did (credential-free by construction). */
    incident: z
      .object({
        identifier: z.string().min(1).optional(),
        url: z.string().min(1).optional(),
        summary: z.string().min(1),
      })
      .strict()
      .optional(),
    /** An AGENT row points at the episode it worked. */
    parentActivityId: zId.optional(),
  })
  .strict();

export const platformActivityOp = fleetReadOp({
  name: "platform.activity",
  permission: "platform:read",
  principals: ["OPERATOR"],
  fields: {},
  response: z
    .object({
      episodes: z.array(zActivityEpisode),
      openCount: z.number().int().nonnegative(),
      resolvedToday: z.number().int().nonnegative(),
      /** How many rows the counts were taken over — the read is bounded. */
      over: z.number().int().nonnegative(),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: {
    episodes: [
      {
        activityId: "act_000001780000000000_a1",
        kind: "ALARM" as const,
        state: "RESOLVED" as const,
        title: "SweeperFn errors on dev",
        alarm: "SweeperFnErrors",
        openedAt: 1_780_000_000_000,
        resolvedAt: 1_780_000_600_000,
        incident: { identifier: "TRA-518", url: "https://linear.app/x/TRA-518", summary: "sweeper horizon throws DuplicatePoolError" },
      },
    ],
    openCount: 0,
    resolvedToday: 1,
    over: 1,
  },
});

/* ------------------------------------------------------------------------- */
/* B94 leg (d) — `login as` (RULED 2026-09-23, Pathik: approved as            */
/* recommended — a short-lived SERVER-SIDE impersonation grant).              */
/* ------------------------------------------------------------------------- */

/**
 * operator.login_as — take a 30-minute, NON-RENEWABLE grant into one
 * business's back office. The crossing wall is the operator's tenant scope;
 * 337-2's row is the audience: ROOT / OPERATOR / SALES (demos only — the
 * handler's wall) / SUPPORT (read-only — the ceiling both walls enforce).
 * The reason is required and lands in THAT business's own audit log beside
 * the operator's id. `bearer` is shown to this caller only; the store keeps
 * a digest. There is no extend door: a longer visit is a second grant with
 * a second reason.
 */
export const operatorLoginAs = tenantCrossingWriteOp({
  name: "operator.login_as",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT", "OPERATOR:OPERATOR", "OPERATOR:SALES", "OPERATOR:SUPPORT"],
  fields: {
    reason: z.string().min(1).max(400),
  },
  response: z
    .object({
      grantId: zId,
      /** `imp1.<grantId>.<secret>.<display>` — the back office's bearer for this visit. */
      bearer: z.string().min(1),
      tenantId: zId,
      expiresAt: zEpochMs,
      readOnly: z.boolean(),
    })
    .strict(),
  exampleRequest: { tenantId: "blue-harbor", clientKey: "ck-login-as-1", reason: "Walking the owner through refunds" },
  exampleResponse: {
    grantId: "g_0123456789abcdef",
    bearer: "imp1.g_0123456789abcdef.s_ABCDEFGHIJKLMNOPQRSTUV",
    tenantId: "blue-harbor",
    expiresAt: 1_780_001_800_000,
    readOnly: false,
  },
});

/**
 * operator.login_as.end — end a visit before its 30 minutes are up. The
 * operator who took the grant, or ROOT. Idempotent: ending an ended grant
 * answers the first end's time. Takes effect on the very next request (the
 * authorizer caches nothing — `authorizerResultTtlInSeconds: 0`).
 */
export const operatorLoginAsEnd = tenantCrossingWriteOp({
  name: "operator.login_as.end",
  permission: "platform:write",
  principals: ["OPERATOR:ROOT", "OPERATOR:OPERATOR", "OPERATOR:SALES", "OPERATOR:SUPPORT"],
  fields: { grantId: zId },
  response: z.object({ grantId: zId, endedAt: zEpochMs }).strict(),
  exampleRequest: { tenantId: "blue-harbor", clientKey: "ck-login-as-end-1", grantId: "g_0123456789abcdef" },
  exampleResponse: { grantId: "g_0123456789abcdef", endedAt: 1_780_000_600_000 },
});
